<template>
  <v-container class="mt-3">
    <v-card>
      <v-card-title
        ><h1 class="text-h6 font-weight-bold ma-0">
          FAANG Resume Guide: How to Get Noticed by Google, Amazon, Meta,
          Apple &amp; Netflix
        </h1></v-card-title
      >
      <v-card-text>
        <resume-cta-banner />
        <v-row>
          <v-col cols="12">
            FAANG companies (Meta, Amazon, Apple, Netflix, Google — and the
            broader group of top-tier tech employers that hire like them)
            receive thousands of applications per open role. Their recruiters
            and hiring committees skim resumes fast, and their applicant
            tracking systems filter out a large share before a human ever sees
            them. This guide covers what these companies actually look for, so
            your resume survives both the software and the skim.
          </v-col>
        </v-row>

        <h2 class="text-h6 mt-4 mb-2">1. Lead With Impact, Not Duties</h2>
        <p>
          FAANG recruiters are trained to look past job titles and
          responsibilities and look for <strong>outcomes</strong>. A bullet like
          "Responsible for backend services" says nothing about your level of
          contribution. Rewrite it around what changed because you did the work:
        </p>
        <ul>
          <li>
            <strong>Weak:</strong> "Worked on checkout service performance."
          </li>
          <li>
            <strong>Strong:</strong> "Redesigned the checkout service's caching
            layer, cutting p99 latency by 38% and reducing infrastructure cost
            by $120K/year."
          </li>
        </ul>

        <h2 class="text-h6 mt-4 mb-2">2. Show Scope and Ownership</h2>
        <p>
          FAANG hiring committees calibrate level (entry, mid, senior, staff)
          largely from scope: did you fix a bug, own a feature, or drive a
          system used by millions? Make scope explicit — team size, user count,
          revenue impact, system reliability, or the number of teams your work
          touched.
        </p>

        <h2 class="text-h6 mt-4 mb-2">
          3. Match the Language of the Job Description
        </h2>
        <p>
          Every FAANG ATS ranks resumes partly on keyword overlap with the job
          posting. If the posting says "distributed systems," don't only write
          "backend systems" — use the same terms the posting uses, as long as
          they honestly describe your experience.
        </p>

        <h2 class="text-h6 mt-4 mb-2">4. Keep the Format Simple</h2>
        <p>
          Multi-column layouts, tables, and text inside graphics often get
          mangled by ATS parsers. Use a single-column layout, standard section
          headers ("Experience", "Education", "Skills"), and export to PDF.
        </p>

        <h2 class="text-h6 mt-4 mb-2">
          5. One Resume Per Company Isn't Required — But Tailoring Is
        </h2>
        <p>
          You don't need five different resumes for five FAANG companies, but
          you should adjust your top bullets and summary for the role and
          company. See our company-specific guides for
          <router-link to="/google-resume-guide">Google</router-link>,
          <router-link to="/meta-resume-guide">Meta</router-link>, and
          <router-link to="/amazon-resume-guide">Amazon</router-link>
          for what each company tends to emphasize. Applying to an early-stage
          company instead? See our
          <router-link to="/tech-startup-resume-guide"
            >tech startup resume guide</router-link
          >.
        </p>

        <h2 class="text-h6 mt-4 mb-2">6. Quantify Everything You Can</h2>
        <p>
          Numbers make claims verifiable and memorable: percentages, dollar
          amounts, user counts, latency, uptime, team size, timeline. A resume
          with five quantified bullets reads as far stronger than one with
          fifteen vague ones.
        </p>

        <v-alert type="info" outlined class="mt-4">
          Tip: Before you apply, run your resume through an ATS-style check to
          catch parsing issues and see how it scores against a real job
          description.
        </v-alert>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import ResumeCtaBanner from "../../components/ResumeCtaBanner.vue";

export default {
  name: "FaangResumeGuide",
  components: { ResumeCtaBanner },
  mounted() {
    this.$utils.visit(this);
  },
};
</script>
